import { BtnDefault } from "@/components/Button";
import { CustomInput as InputForm } from "@/components/Input";
import { useLocality } from "@/hooks/Client/useLocality";
import { Address } from "@/types";
import { useFilter } from "@/utils";
import {
  Collapse,
  Flex,
  Text,
  useBreakpointValue,
  useToast
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

function FormAddressCollapse({
  IsEditingAddress,
  setIsEditingAddress,
  handleDeliveryAddress
}) {
  const Wide = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: false,
    lg: true,
    xl: true
  });
  const [Zipcode, setZipcode] = useState("");
  const [Street, setStreet] = useState("");
  const [Number, setNumber] = useState("");
  const [Complement, setComplement] = useState("");
  const [District, setDistrict] = useState("");
  const [City, setCity] = useState("");
  const [State, setState] = useState("");
  const [HasValidNewAddress, setHasValidNewAddress] = useState(false);

  const Toast = useToast({
    position: "bottom",
    duration: 5000,
    isClosable: true
  });

  const fillAddress = async (Zip) => {
    await useLocality({ zipcode: Zip }).then(([Data, Error, Loading]) => {
      if (Data && Error === null) {
        setZipcode(Zip);
        setStreet(Data.street);
        setDistrict(Data.district);
        setCity(Data.city);
        setState(Data.state);
        setHasValidNewAddress(true);
      } else {
        Toast({
          title: Error,
          description: "Tente reacarregar a página ou contate o suporte",
          status: "error"
        });
      }
    });
  };

  const sendData = async () => {
    const Addr: Address = {
      cep: Zipcode,
      bairro: District,
      numero: Number,
      complemento: Complement,
      cidade: City,
      latitude: 0,
      longitude: 0,
      rua: Street,
      tipoEndereco: "ENTREGA",
      uf: State,
      codIbge: "0"
    };
    await handleDeliveryAddress(Addr);
  };

  useEffect(() => {
    const Zip = useFilter(Zipcode, { type: "number" }) ?? "";
    if (Zip.length === 8) {
      fillAddress(Zip);
    }
  }, [Zipcode]);

  return (
    <Collapse in={IsEditingAddress} animateOpacity>
      <Flex
        bgColor={"whiteAlpha.100"}
        as={"form"}
        my={"14px"}
        mx={"10px"}
        maxW={Wide && "500px"}
        direction={"column"}
        gap={"10px"}
      >
        {/* ZIPCODE & DESCRIPTION */}
        <Flex w={"full"} alignItems={"center"}>
          {/* # ZIPCODE */}
          <Flex width={"50%"}>
            <InputForm
              name={"zipcode"}
              p={"0 0 0 10px"}
              mask={"99.999-999"}
              type={"text"}
              width={"full"}
              height={"40px"}
              size={"sm"}
              value={Zipcode}
              onChange={(e) => setZipcode(e.target.value)}
              placeholder={"CEP..."}
            />
          </Flex>
          {/* # DESCRIPTION */}
          <Text ml={"1rem"} color={"gray.300"} fontSize={"12px"} flex={1}>
            {"Insira seu CEP para"}
            <br />
            {"preencher o endereço"}
          </Text>
        </Flex>

        {/* STREET */}
        <Flex>
          <InputForm
            isDisabled={true}
            name={"street"}
            p={"0 0 0 10px"}
            type={"text"}
            width={"full"}
            height={"40px"}
            size={"sm"}
            value={Street}
            placeholder={"Rua..."}
          />
        </Flex>

        {/* NUMBER & COMPLEMENT */}
        <Flex gap={"10px"}>
          {/* # NUMBER */}
          <Flex width={"50%"}>
            <InputForm
              name={"number"}
              p={"0 0 0 10px"}
              type={"number"}
              width={"full"}
              height={"40px"}
              size={"sm"}
              value={Number}
              isDisabled={!HasValidNewAddress}
              onChange={(e) => {
                setNumber(e.target.value);
              }}
              placeholder={"Número..."}
            />
          </Flex>

          {/* # COMPLEMENT */}
          <Flex width={"50%"}>
            <InputForm
              name={"complement"}
              p={"0 0 0 10px"}
              type={"text"}
              width={"full"}
              height={"40px"}
              size={"sm"}
              value={Complement}
              isDisabled={!HasValidNewAddress}
              onChange={(e) => setComplement(e.target.value)}
              placeholder={"Complemento..."}
            />
          </Flex>
        </Flex>

        {/* NEIGHBORHOOD, CITY & STATE */}
        <Flex gap={"10px"}>
          {/* # NEIGHBORHOOD */}
          <Flex width={"33.3333%"}>
            <InputForm
              isDisabled={true}
              name={"district"}
              p={"0 0 0 10px"}
              type={"text"}
              width={"full"}
              height={"40px"}
              size={"sm"}
              value={District}
              placeholder={"Bairro..."}
            />
          </Flex>

          {/* # CITY */}
          <Flex width={"33.3333%"}>
            <InputForm
              isDisabled={true}
              name={"city"}
              p={"0 0 0 10px"}
              type={"text"}
              width={"full"}
              height={"40px"}
              size={"sm"}
              value={City}
              placeholder={"Cidade..."}
            />
          </Flex>

          {/* # STATE */}
          <Flex width={"33.3333%"}>
            <InputForm
              isDisabled={true}
              name={"UF"}
              p={"0 0 0 10px"}
              type={"text"}
              width={"full"}
              height={"40px"}
              size={"sm"}
              value={State}
              placeholder={"Estado..."}
            />
          </Flex>
        </Flex>

        {/* BUTTONS */}
        <Flex gap={"10px"}>
          {/* # CANCEL */}
          <BtnDefault
            w={"50%"}
            colorScheme={"gray"}
            bgColor={"gray.500"}
            onClick={() => setIsEditingAddress(!IsEditingAddress)}
            children={"CANCELAR"}
          />

          {/* # SAVE */}
          <BtnDefault
            w={"50%"}
            colorScheme={"blue"}
            bgColor={"blue.500"}
            children={"ALTERAR"}
            isDisabled={!HasValidNewAddress}
            onClick={sendData}
          />
        </Flex>
      </Flex>
    </Collapse>
  );
}

export { FormAddressCollapse };
